// import actions
import {
  fetchHolidays,
  setUserPreference
} from '../../../redux/actionCreators';

// import atoms
import { employeeEntityIdsFilterAtom, planUnitsAtom } from './atoms';

// import components
import DialogManager from './components/DialogManager/DialogManager';
import Header from './components/Header/Header';
import MapView from './AzureMaps/MapView';
import CardsView from './CardsView/CardsView';
import ListView from './ListView/ListView';
import RosterView from './RosterView';
import TimelineView from './TimelineView';
import { AppointmentContextMenuManager } from './components';

// import enums
import { View, ViewContext } from './enums';

// import hooks
import { useEffect } from 'react';
import { useDispatch, useNavParams, useSelector } from 'hooks';
import { RecoilRoot, useRecoilState } from 'recoil';
import usePlanning from './hooks/usePlanning';
import usePlanningEntity from './hooks/usePlanningEntity';

// import types
import { PlanUnitMix } from 'components/types';
import { ActivityPlanningProps } from './ActivityPlanning.types';

// import utils
import { getViewContext } from './utils';
import { getCurrentlyLoggedInEmployeeEntityId } from 'utils/misc';
import { StyledActivityPlanningBox } from './ActivityPlanning.styles';

function ActivityPlanning({ page, isEmployeeRole }: ActivityPlanningProps) {
  const dispatch = useDispatch();
  const { defaultActivityPlanningView } = useSelector(
    (state) => state.preferences
  );
  const {
    hasActivityPlanningViewAccess,
    hasWorkPlacePlanningViewAccess,
    hasMyActivitiesAccess,
    hasManageOwnProjectsAccess,
    hasProjectTeamAccess,
    hasKidsPlanAccess
  } = useSelector(({ grants }) => grants);
  const [, setSelectedPlanUnits] = useRecoilState(planUnitsAtom);
  const [, setEmployeeEntityIds] = useRecoilState(employeeEntityIdsFilterAtom);

  const { params } = useNavParams();
  const view = params?.view as View;

  const [planningEntityTypePreference] = usePlanningEntity();
  const planningEntityType = planningEntityTypePreference[view];

  useEffect(() => {
    dispatch(fetchHolidays());
  }, [dispatch]);

  useEffect(() => {
    if (params && !view) {
      const defaultView = Object.values(View).includes(
        defaultActivityPlanningView
      )
        ? defaultActivityPlanningView
        : View.Roster;
      window.location.href = `#${page}&view=${defaultView}`;
    }
  }, [params]);

  useEffect(() => {
    if (getViewContext() === ViewContext.Own) {
      setEmployeeEntityIds([getCurrentlyLoggedInEmployeeEntityId()]);
    }
  }, []);

  const handleSelectPlanUnit = (options: PlanUnitMix[]) => {
    setSelectedPlanUnits(options);
  };

  const handleChangeView = (view: View) => {
    if (!view) return;

    dispatch(
      setUserPreference({
        key: 'defaultActivityPlanningView',
        value: view
      })
    );
  };

  const showEvents =
    planningEntityType != 'EMPLOYEE' || hasActivityPlanningViewAccess;

  const planning = usePlanning(view as View, showEvents);
  const context = getViewContext();

  return (
    <StyledActivityPlanningBox
      component="section"
      overflowX={view === View.Roster ? 'hidden' : 'initial'}
      overflowY={view === View.Timeline ? 'hidden' : 'initial'}
    >
      <Header
        view={view as View}
        handleSelectPlanUnit={handleSelectPlanUnit}
        handleChangeView={handleChangeView}
        isEmployeeRole={isEmployeeRole}
      />
      {(hasActivityPlanningViewAccess ||
        hasWorkPlacePlanningViewAccess ||
        (hasMyActivitiesAccess && context == ViewContext.Own) ||
        hasManageOwnProjectsAccess ||
        hasProjectTeamAccess ||
        hasKidsPlanAccess) &&
        view === View.Roster && (
          <RosterView
            isMyActivities={context === ViewContext.Own}
            {...planning}
          />
        )}
      {(hasActivityPlanningViewAccess ||
        hasWorkPlacePlanningViewAccess ||
        (hasMyActivitiesAccess && context == ViewContext.Own) ||
        hasManageOwnProjectsAccess ||
        hasProjectTeamAccess) &&
        view === View.List && (
          <ListView
            data={planning.data}
            isLoading={planning.isLoading}
            employees={planning.employees}
            isMyActivities={context === ViewContext.Own}
          />
        )}
      {hasActivityPlanningViewAccess && (
        <>
          {view === View.Timeline && <TimelineView {...planning} />}
          {view === View.Cards && <CardsView {...planning} />}
          {view === View.Map && (
            <MapView planningEntities={planning.planningEntities} />
          )}
        </>
      )}
      {planning.planningEntities && (
        <AppointmentContextMenuManager
          planningEntities={planning.planningEntities}
          isEmployeePerspective={planningEntityType === 'EMPLOYEE'}
        />
      )}
    </StyledActivityPlanningBox>
  );
}

function ActivityPlanningContainer(props) {
  return (
    <RecoilRoot>
      <ActivityPlanning {...props} />
      <DialogManager />
    </RecoilRoot>
  );
}

export default ActivityPlanningContainer;
